import { useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, MessageBar, MessageBarBody, Spinner,
} from '@fluentui/react-components';
import { useMutation } from '@tanstack/react-query';
import { rotateSecret, type RotatableSecret } from '../../api/admin.api';
import { useT } from '../../i18n';

interface Props {
  /** Clave a rotar; null = diálogo cerrado. */
  secret: RotatableSecret | null;
  onClose: () => void;
  /** Se llama tras una rotación exitosa para refrescar el estado del sistema. */
  onRotated: () => void;
}

/** SUPER_ADMIN: pega el valor nuevo de una clave (IA, bot de Campo, GitHub). No se guarda en el navegador. */
export function RotateBotTokenDialog({ secret, onClose, onRotated }: Props) {
  const t = useT();
  const [value, setValue] = useState('');

  const mutation = useMutation({
    mutationFn: () => rotateSecret(secret!.key, value.trim()),
    onSuccess: (res) => { if (res.ok) { setValue(''); onRotated(); } },
  });

  const close = () => { setValue(''); mutation.reset(); onClose(); };
  const result = mutation.data;
  const done = result?.ok === true;

  return (
    <Dialog open={!!secret} onOpenChange={(_, d) => { if (!d.open) close(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t.settings.rotateTokenTitle.replace('{name}', secret?.label ?? '')}</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {!done && <>
              <span>{t.settings.rotateTokenHint}</span>
              <Field label={t.settings.rotateTokenLabel}>
                <Input type="password" autoComplete="off" value={value} onChange={(_, d) => setValue(d.value)} disabled={mutation.isPending} />
              </Field>
            </>}
            {done && result.ok && (
              <MessageBar intent="success"><MessageBarBody>
                {result.username ? t.settings.rotateTokenOkBot.replace('{bot}', `@${result.username}`) : t.settings.rotateTokenOk}
                {result.lastError ? ` (${result.lastError})` : ''}
              </MessageBarBody></MessageBar>
            )}
            {result && !result.ok && <MessageBar intent="error"><MessageBarBody>{result.message}</MessageBarBody></MessageBar>}
            {mutation.isError && <MessageBar intent="error"><MessageBarBody>{t.settings.rotateTokenError}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>{done ? t.common.close : t.common.cancel}</Button>
            {!done && (
              <Button appearance="primary" disabled={!value.trim() || mutation.isPending} onClick={() => mutation.mutate()}
                icon={mutation.isPending ? <Spinner size="tiny" /> : undefined}>
                {t.settings.rotateTokenSubmit}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
